import React, { useState } from 'react';
import { Field, Form } from 'react-final-form';
import Axios from 'axios';
import Select from 'react-select-5';
import Modal, { Footer } from '@/components/common/Modal';
import * as Routes from '@/modules/routes';
import { ISeatPool, ISubscriber } from './utils';

const GRADES = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

const gradeOptions = [
  { label: 'None', value: '' },
  ...GRADES.map(grade => ({ label: grade, value: grade })),
];

interface GradeEditModalProps {
  modalManager: ModalManager;
  seatPool: ISeatPool;
  subscriber: ISubscriber;
  onGradeUpdate: (seatPoolId: number, newGrade: string) => void;
  useStateSpecificProgramGrades?: boolean;
}

const GradeEditModal = ({
  modalManager, seatPool, onGradeUpdate, useStateSpecificProgramGrades = false,
}: GradeEditModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = (values: { grade: string }) => {
    setIsSubmitting(true);

    return Axios
      .patch(Routes.plato_api_seat_pool_path(seatPool.id), {
        seat_pool: { grade: values.grade || null },
      })
      .then(() => {
        onGradeUpdate(seatPool.id, values.grade);
        modalManager.close();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <Modal
      allowOverflow
      closeModal={modalManager.close}
      headerClass="tw-pr-10"
      headerText={`${useStateSpecificProgramGrades ? 'Override' : 'Set'} default grade for ${seatPool.program.full_title_with_edition}`}
      isLoading={isSubmitting}
      isOpen={modalManager.isOpen}
    >
      <Form
        initialValues={{ grade: seatPool.grade || '' }}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit}>
            <div className="tw-mb-4">
              <Field name="grade">
                {({ input, meta }) => (
                  <div>
                    {useStateSpecificProgramGrades && (
                      <>
                        <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2" htmlFor="grade">
                          Default Grade
                        </label>
                        <Select
                          isDisabled
                          className="tw-text-sm tw-mb-5"
                          components={{ DropdownIndicator: () => null }}
                          placeholder={seatPool.program.grade}
                        />
                      </>
                    )}
                    <label className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2" htmlFor="grade">
                      Grade (K-12)
                    </label>
                    <Select
                      {...input}
                      isClearable
                      className="tw-text-sm"
                      classNamePrefix="react-select"
                      options={gradeOptions}
                      placeholder="Select a grade"
                      value={gradeOptions.find(option => option.value === input.value) || null}
                      onChange={selectedOption => input.onChange(selectedOption?.value || '')}
                    />
                    {meta.error && meta.touched && (
                      <span className="tw-text-red-600 tw-text-sm">{meta.error}</span>
                    )}
                  </div>
                )}
              </Field>
            </div>
            <Footer
              primaryButtonClassName="btn btn--purple"
              primaryButtonText={isSubmitting ? 'Saving...' : 'Save'}
              secondaryButtonCallback={modalManager.close}
              secondaryButtonText="Cancel"
              submitting={isSubmitting}
            />
          </form>
        )}
        onSubmit={onSubmit}
      />
    </Modal>
  );
};

export default GradeEditModal;
